import type { Theme, SxProps, Breakpoint } from "@mui/material/styles";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import Drawer, { drawerClasses } from "@mui/material/Drawer";
import { useTheme } from "@mui/material/styles";
import { usePathname } from "routes/hooks";
import { RouterLink } from "routes/components";
import { varAlpha } from "theme/styles";
import Logo from "components/logo/logo";
import { Scrollbar } from "components/scrollbar";
import { AppVersion } from "../components/app-version";
import type { WorkspacesPopoverProps } from "../components/workspaces-popover";
import { ChevronLeft, KeyboardArrowDown } from "@mui/icons-material";
import { Typography } from "@mui/material";

// ----------------------------------------------------------------------

export type NavItem = {
  path: string;
  title: string;
  icon?: React.ReactNode;
  info?: React.ReactNode;
  children?: NavItem[];
};

export type NavContentProps = {
  data: NavItem[];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  workspaces: WorkspacesPopoverProps["data"];
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

export function NavDesktop({
  sx,
  data,
  slots,
  workspaces,
  layoutQuery,
}: NavContentProps & { layoutQuery: Breakpoint }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        pt: 2.5,
        px: 2.5,
        top: 0,
        left: 0,
        height: 1,
        display: "none",
        position: "fixed",
        flexDirection: "column",
        bgcolor: "var(--layout-nav-bg)",
        zIndex: "var(--layout-nav-zIndex)",
        width: "var(--layout-nav-vertical-width)",
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(
          theme.vars.palette.grey["500Channel"],
          0.12
        )})`,
        [theme.breakpoints.up(layoutQuery)]: {
          display: "flex",
        },
        ...sx,
      }}
    >
      <NavContent data={data} slots={slots} workspaces={workspaces} />
    </Box>
  );
}

// ----------------------------------------------------------------------

export function NavMobile({
  sx,
  data,
  open,
  slots,
  onClose,
  workspaces,
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      open={open}
      anchor="left"
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          pt: 2.5,
          px: 2.5,
          overflow: "unset",
          bgcolor: "var(--layout-nav-bg)",
          width: "var(--layout-nav-mobile-width)",
          ...sx,
        },
      }}
    >
      <NavContent data={data} slots={slots} workspaces={workspaces} />
    </Drawer>
  );
}

// ----------------------------------------------------------------------

export function NavContent({ data, slots, workspaces, sx }: NavContentProps) {
  const pathname = usePathname();

  return (
    <>
      <Box
        sx={{
          padding: "0px 16px",
        }}
      >
        <Logo width={72} />
      </Box>

      {slots?.topArea}

      {/* <WorkspacesPopover data={workspaces} sx={{ my: 2 }} /> */}
      <Scrollbar fillContent>
        <Box
          component="nav"
          display="flex"
          flex="1 1 auto"
          flexDirection="column"
          sx={sx}
        >
          <Box
            component="ul"
            gap={0.5}
            display="flex"
            flexDirection="column"
            // sx={{ pl: 0 }}
            sx={{
              pl: 0,

              maxHeight: "calc(100vh - 102px)", // Logo area height is around 102px
            }}
          >
            {data.map((item) => (
              <NavItemComponent
                key={item.title}
                item={item}
                pathname={pathname}
              />
            ))}
          </Box>
        </Box>
      </Scrollbar>

      {slots?.bottomArea}

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "var(--layout-nav-bg)",
          zIndex: 1,
          borderTop: "1px solid",
          borderColor: "var(--layout-nav-border-color, rgba(0,0,0,0.12))",
        }}
      >
        <AppVersion />
      </Box>
    </>
  );
}

// ----------------------------------------------------------------------

function NavItemComponent({
  item,
  pathname,
  depth = 0,
}: {
  item: NavItem;
  pathname: string;
  depth?: number;
}) {
  const [open, setOpen] = useState<boolean>(() => {
    return item.children?.some((c) => c.path === pathname) || false;
  });
  const isActive = pathname === item.path;
  // pathname.startsWith(item.path + "/")
  // item.children?.some(
  //   (child) =>
  //     pathname === child.path || pathname.startsWith(child.path + "/")
  // );

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const hasChildren = Boolean(item.children?.length);

  return (
    <>
      <ListItem disableGutters disablePadding>
        <ListItemButton
          disableGutters
          component={hasChildren ? "div" : RouterLink}
          href={hasChildren ? undefined : item.path}
          onClick={hasChildren ? handleToggle : undefined}
          sx={{
            pl: 2 + depth * 2,
            py: 1,
            gap: 2,
            pr: 1.5,
            borderRadius: 0.75,
            typography: "body2",
            fontWeight: "fontWeightMedium",
            color: "var(--layout-nav-item-color)",
            minHeight: "var(--layout-nav-item-height)",
            ...(isActive && {
              fontWeight: "fontWeightSemiBold",
              bgcolor: "var(--layout-nav-item-active-bg)",
              color: "var(--layout-nav-item-active-color)",
              "&:hover": {
                bgcolor: "var(--layout-nav-item-hover-bg)",
              },
            }),
          }}
        >
          <Box component="span" sx={{ width: 24, height: 24 }}>
            {item.icon}
          </Box>

          <Box component="span" flexGrow={1}>
            {item.title}
          </Box>

          {item.info && item.info}

          {hasChildren && (
            <Box component="span">
              {open ? <KeyboardArrowDown /> : <ChevronLeft />}
            </Box>
          )}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Box sx={{ width: "100%" }}>
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit
            sx={{
              // ensure collapsed content participates in layout
              display: "block",
            }}
          >
            <Box component="ul" sx={{ pl: 0 }}>
              {item.children!.map((child) => (
                <NavItemComponent
                  key={child.title}
                  item={child}
                  pathname={pathname}
                  depth={depth + 1}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </>
  );
}
